import { createClient, type SupabaseClient as RawClient } from '@supabase/supabase-js';
import type { Database } from './database.types';

export type SupabaseClient = RawClient<Database>;
export type { Database };

/** What Supabase needs of a session store. `localStorage` fits it. */
export interface SupabaseAuthStorage {
  getItem(key: string): string | null | Promise<string | null>;
  setItem(key: string, value: string): void | Promise<void>;
  removeItem(key: string): void | Promise<void>;
}

export interface SupabaseClientOptions {
  url: string;
  anonKey: string;
  /**
   * Storage key for the auth session. REQUIRED (M2) — each app MUST supply a
   * unique key so that admin and storefront sessions don't collide on the
   * same origin. Recommended: 'dukanti-admin-auth' / 'dukanti-storefront-auth'.
   */
  storageKey: string;
  /**
   * Whether Supabase should automatically consume auth callback parameters
   * from the current browser URL. Defaults to true for backwards
   * compatibility; apps with route-owned callbacks should pass a route check.
   */
  detectSessionInUrl?: boolean;
  /**
   * Defaults to true. A storefront document embedded in Admin passes false
   * (D-P0-2): it is anonymous and must never load the shopper's session.
   */
  persistSession?: boolean;
  /** Defaults to true. */
  autoRefreshToken?: boolean;
  /** Defaults to the Supabase default (`localStorage` in a browser). */
  storage?: SupabaseAuthStorage;
  /** Defaults to the global `fetch`. C14: the embedded canvas passes its read-only fetch. */
  fetch?: typeof fetch;
}

type CreateClientOptions = NonNullable<Parameters<typeof createClient<Database>>[2]>;

/** Pure: the exact options `createSupabaseClient` hands to Supabase. */
export function buildSupabaseClientConfig(opts: SupabaseClientOptions): CreateClientOptions {
  return {
    auth: {
      persistSession: opts.persistSession ?? true,
      autoRefreshToken: opts.autoRefreshToken ?? true,
      detectSessionInUrl: opts.detectSessionInUrl ?? true,
      // M2: PKCE avoids leaking tokens via URL fragments and is the
      // recommended flow for SPAs per Supabase docs.
      flowType: 'pkce',
      storageKey: opts.storageKey,
      ...(opts.storage === undefined ? {} : { storage: opts.storage }),
    },
    ...(opts.fetch === undefined ? {} : { global: { fetch: opts.fetch } }),
  };
}

export function createSupabaseClient(opts: SupabaseClientOptions): SupabaseClient {
  return createClient<Database>(opts.url, opts.anonKey, buildSupabaseClientConfig(opts));
}
